import React from "react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const COLORS = ["#00ff90","#60a5fa","#c084fc","#fbbf24","#f87171","#34d399","#fb923c"];
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

export default function OrgComposition({ loading, data }) {
  const byDept     = data?.byDept     || [];
  const byLocation = data?.byLocation || [];
  const byType     = data?.byType     || [];

  const donutData = byDept.map((r, i) => ({
    name: r.dept, value: r.pct ?? r.count ?? 0, color: COLORS[i % COLORS.length],
  }));

  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Org Composition</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Org Composition</span>
        {data?.totalHeadcount && (
          <span className="text-xs text-white/30 border border-white/10 px-2 py-0.5 rounded-full">{data.totalHeadcount} total</span>
        )}
      </div>
      <div className="p-5 space-y-5">

        {/* Dept donut + legend */}
        {donutData.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-3">By Department</p>
            <div className="flex items-center gap-4">
              <ResponsiveContainer width={120} height={120}>
                <PieChart>
                  <Pie data={donutData} cx="50%" cy="50%" innerRadius={32} outerRadius={52} paddingAngle={2} dataKey="value">
                    {donutData.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.85} />)}
                  </Pie>
                  <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v}%`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1.5">
                {donutData.map(d => (
                  <div key={d.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                      <span className="text-xs text-white/50">{d.name}</span>
                    </div>
                    <span className="text-xs font-medium" style={{ color: d.color }}>{d.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* By location bar */}
        {byLocation.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">By Location</p>
            <ResponsiveContainer width="100%" height={80}>
              <BarChart data={byLocation} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                <YAxis type="category" dataKey="location" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.5)" }} width={65} />
                <Tooltip {...tooltipStyle} formatter={v => [`${v}%`, "Share"]} />
                <Bar dataKey="pct" fill="#00ff90" fillOpacity={0.6} radius={[0,3,3,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* By type pills */}
        {byType.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">By Employment Type</p>
            <div className="flex gap-2 flex-wrap">
              {byType.map((t, i) => (
                <span key={i} className="text-xs px-3 py-1 rounded-full border border-[#00ff90]/20 bg-[#00ff90]/5"
                  style={{ color: COLORS[i % COLORS.length] }}>
                  {t.type}: {t.count}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}